import type { ReactNode } from 'react';
import { useNavigate } from '@tanstack/react-router';
import {
  Award,
  BellRing,
  ChefHat,
  ChevronRight,
  MessageSquareWarning,
  ShoppingBag,
  UserPlus,
  Users,
  Utensils,
} from 'lucide-react';
import {
  Card,
  DashboardRow,
  DataTable,
  ErrorState,
  GrainPicker,
  LoadingState,
  MetricCard,
  MetricRow,
  PanelTitle,
  RangePicker,
  Stat,
  StatusPill,
  type DataTableColumn,
} from '../../components';
import { useSession } from '../../lib/auth/useSession';
import { formatCount } from '../../lib/format/money';
import { formatDate, formatDateTime } from '../../lib/format/dates';
import { useOpenAlertCount } from '../alerts/queries';
import { useFeedbackList } from '../feedback/queries';
import type { NeverPublished } from '../profiles/api';
import { useNeverPublished, useProfileCount } from '../profiles/queries';
import { ProfileCell } from '../profiles/people';
import { QueryCard, SeriesPair, formatHours, rangeTitle, useAnalyticsWindow } from '../analytics/parts';
import { useDishSeries, useSnapshot } from '../analytics/queries';
import type { PlatformSnapshot } from '../analytics/api';

/** Navigates to a nav screen. Nav routes are built from NAV at runtime, so their paths aren't in the router's type union. */
function useGo() {
  const navigate = useNavigate();
  return (to: string, search?: Record<string, string>) => void navigate({ to: to as never, search: search as never });
}
const NEVER_PUBLISHED_LIMIT = 500;

/**
 * Main KPIs (kit `DashboardScreen`; FE plan §7 Dashboard). Snapshot tiles,
 * 14-day published vs sold, "Needs your attention" from live counts, and
 * time-to-first-dish.
 */
export function DashboardScreen() {
  // One window for the page — Platform at a glance and the chart — 7d by default
  // (URL ?range= / ?bucket=, the standard range control).
  const chart = useAnalyticsWindow({ range: '7d' });
  const days = { start: chart.window.startDate, end: chart.window.endDate };
  const snapshot = useSnapshot([], days);
  const listed = useDishSeries('listed', chart.window);
  const sold = useDishSeries('sold', chart.window);

  return (
    <div className="flex flex-col gap-5">
      <div>
        <PanelTitle eyebrow="Platform snapshot" actions={<RangePicker value={chart.range} onChange={chart.setRange} />}>
          Platform at a glance
        </PanelTitle>
        {snapshot.isError ? (
          <ErrorState error={snapshot.error} onRetry={() => void snapshot.refetch()} compact />
        ) : (
          <SnapshotTiles data={snapshot.data?.snapshot} rangeLabel={rangeTitle(chart.range).toLowerCase()} />
        )}
        {snapshot.data && <div className="text-xs text-muted">As of {formatDateTime(snapshot.data.snapshot.asOf)}</div>}
      </div>

      <DashboardRow cols="minmax(0,1.5fr) minmax(0,1fr)" height="default">
        <Card
          surface="panel"
          scroll
          header={
            <div className="flex-1">
              <PanelTitle eyebrow="Dishes published vs. sold">{rangeTitle(chart.range)}</PanelTitle>
              <GrainPicker value={chart.bucket} onChange={chart.setBucket} />
            </div>
          }
        >
          {listed.isPending || sold.isPending ? (
            <LoadingState />
          ) : listed.isError || sold.isError ? (
            <ErrorState
              error={listed.error ?? sold.error}
              onRetry={() => void Promise.all([listed.refetch(), sold.refetch()])}
              compact
            />
          ) : (
            <>
              <SeriesPair
                a={listed.data.series}
                b={sold.data.series}
                aLabel="Dishes listed"
                bLabel="Portions sold"
                bucket={chart.bucket}
                height={150}
              />
            </>
          )}
        </Card>
        <NeedsAttention />
      </DashboardRow>

      <TimeToFirstDish />
    </div>
  );
}

/** Active chefs and registered eaters are now; every other figure is over the page's range. */
function SnapshotTiles({ data: s, rangeLabel }: { data?: PlatformSnapshot; rangeLabel: string }) {
  const n = (v: number | undefined) => (s ? formatCount(v) : '…');
  const newChefs = s?.newChefSignups;
  const newEaters = s?.newEaterSignups;
  return (
    <MetricRow>
      <MetricCard
        label="Active chefs"
        value={n(s?.activeChefs)}
        delta={newChefs !== undefined ? `+${formatCount(newChefs)}` : undefined}
        deltaTone="neutral"
        hint={`new chefs · ${rangeLabel}`}
        accent="var(--admin-chart-1)"
        icon={<ChefHat size={15} aria-hidden="true" />}
      />
      <MetricCard
        label="Registered eaters"
        value={n(s?.totalEaters)}
        delta={newEaters !== undefined ? `+${formatCount(newEaters)}` : undefined}
        deltaTone="neutral"
        hint={`new · ${rangeLabel}`}
        accent="var(--admin-chart-2)"
        icon={<Users size={15} aria-hidden="true" />}
      />
      <MetricCard
        label="Dishes listed"
        value={n(s?.dishesListed)}
        hint={rangeLabel}
        accent="var(--admin-chart-3)"
        icon={<Utensils size={15} aria-hidden="true" />}
      />
      <MetricCard
        label="Portions sold"
        value={n(s?.dishesSold)}
        hint={`completed orders · ${rangeLabel}`}
        accent="var(--admin-chart-4)"
        icon={<ShoppingBag size={15} aria-hidden="true" />}
      />
    </MetricRow>
  );
}

// --- needs your attention ---------------------------------------------------------

type Tone = 'danger' | 'warning' | 'info';
const TONE: Record<Tone, string> = {
  danger: 'bg-[rgba(178,58,46,.1)] text-[#B23A2E]',
  warning: 'bg-[rgba(180,158,49,.14)] text-[#7A5A12]',
  info: 'bg-[rgba(127,150,254,.16)] text-[#223679]',
};

interface AttentionItem {
  key: string;
  count: number | undefined;
  capped?: boolean;
  title: (n: string) => string;
  body: string;
  icon: ReactNode;
  tone: Tone;
  /** Undefined when the user can't open the screen behind it — the count still shows. */
  go?: () => void;
  lockedReason?: string;
}

function NeedsAttention() {
  const go = useGo();
  const { can } = useSession();
  // Everyone on this screen sees the count; only alert readers can open the list.
  const alertsReadable = can('money.read') || can('alerts.bsc.read') || can('alerts.campaign.read');
  const alerts = useOpenAlertCount();
  const followUp = useFeedbackList({ triageStatus: 'follow_up', page: 1, pageSize: 1 }, { silent: true });
  const pending = useProfileCount({ hasPublishedListing: true, blueStarStatus: 'pending' });
  const never = useNeverPublished([]);
  const neverCount = never.data?.chefsNeverPublished.length;

  const items: AttentionItem[] = [
    {
      key: 'alerts',
      count: alerts.isError ? 0 : alerts.data,
      title: (n: string) => `${n} open alert${n === '1' ? '' : 's'}`,
      body: alertsReadable ? 'Unacknowledged alerts.' : 'Unacknowledged alerts — let an admin know.',
      icon: <BellRing size={16} aria-hidden="true" />,
      tone: 'danger' as const,
      go: alertsReadable ? () => go('/money/alerts') : undefined,
      lockedReason: alertsReadable ? undefined : 'Opening alerts needs an admin role',
    },
    {
      key: 'feedback',
      count: followUp.data?.total,
      title: (n) => `${n} feedback item${n === '1' ? '' : 's'} marked follow-up`,
      body: 'Eater feedback waiting on a follow-up.',
      icon: <MessageSquareWarning size={16} aria-hidden="true" />,
      tone: 'warning',
      go: () => go('/feedback'),
    },
    {
      key: 'blue-star',
      count: pending.data,
      title: (n) => `${n} Blue Star application${n === '1' ? '' : 's'} pending`,
      body: 'Chefs waiting on a certification decision.',
      icon: <Award size={16} aria-hidden="true" />,
      tone: 'info',
      go: () => go('/chefs', { blueStarStatus: 'pending' }),
    },
    {
      key: 'no-first-dish',
      count: neverCount,
      capped: neverCount === NEVER_PUBLISHED_LIMIT,
      title: (n) => `${n} signed up, no dish yet`,
      body: 'Consumer profiles that have never published.',
      icon: <UserPlus size={16} aria-hidden="true" />,
      tone: 'warning',
      go: () => go('/chefs', { view: 'no-first-dish' }),
    },
  ];

  const loading = items.some((i) => i.count === undefined);
  const open = items.filter((i) => (i.count ?? 0) > 0);

  return (
    <Card
      surface="panel"
      scroll
      header={
        <div className="flex-1">
          <PanelTitle eyebrow="Alerts & action items">Needs your attention</PanelTitle>
        </div>
      }
    >
      {loading && !open.length ? (
        <LoadingState />
      ) : !open.length ? (
        <div className="text-[13px] text-muted">Nothing needs attention.</div>
      ) : (
        <div className="flex flex-col gap-2.5">
          {open.map((i) => {
            const n = `${formatCount(i.count)}${i.capped ? '+' : ''}`;
            return (
              <button
                key={i.key}
                type="button"
                onClick={i.go}
                aria-disabled={!i.go || undefined}
                title={i.lockedReason}
                className={`flex w-full items-start gap-[11px] rounded-[10px] border border-[rgba(143,68,32,.1)] bg-transparent px-3 py-[11px] text-left font-body ${i.go ? 'hover:bg-[rgba(143,68,32,.04)]' : 'cursor-default'}`}
              >
                <span
                  className={`flex h-[30px] w-[30px] shrink-0 items-center justify-center rounded-lg ${TONE[i.tone]}`}
                >
                  {i.icon}
                </span>
                <span className="min-w-0">
                  <span className="block text-[13.5px] font-semibold leading-[1.4] text-brown-deep">{i.title(n)}</span>
                  <span className="mt-0.5 block text-xs text-muted">{i.body}</span>
                </span>
                {i.go && <ChevronRight size={15} aria-hidden="true" className="ml-auto shrink-0 text-muted" />}
              </button>
            );
          })}
        </div>
      )}
    </Card>
  );
}

// --- time to first dish -------------------------------------------------------------------

const NEVER_COLUMNS: DataTableColumn<NeverPublished>[] = [
  { id: 'profile', header: 'Profile', width: '2fr', cell: (r) => <ProfileCell name={r.displayName} /> },
  {
    id: 'signup',
    header: 'Signed up',
    width: '1fr',
    cell: (r) => <span className="text-muted">{formatDate(r.signupAt)}</span>,
  },
  {
    id: 'waiting',
    header: 'Time to first dish',
    width: '170px',
    cell: (r) => (
      <StatusPill tone="warning" dot>
        {formatHours(r.hoursSinceSignup)} and counting
      </StatusPill>
    ),
  },
];

function TimeToFirstDish() {
  const go = useGo();
  const never = useNeverPublished([]);
  const count = never.data?.chefsNeverPublished.length ?? 0;

  return (
    <QueryCard eyebrow="Time-to-first-dish" title="Signup → first published dish" scroll={false} query={never}>
      {(d) => (
        <>
          <div className="mb-4 grid grid-cols-3 gap-2.5">
            <Stat label="Average TTFD" value={formatHours(d.avgHoursToFirstDish)} hint="chefs who have published" />
            <Stat label="Chefs measured" value={formatCount(d.chefsMeasured)} />
            <Stat
              label="Signed up, no dish"
              value={`${formatCount(count)}${count === NEVER_PUBLISHED_LIMIT ? '+' : ''}`}
              hint="oldest signups below"
              action={
                <button
                  type="button"
                  onClick={() => go('/chefs', { view: 'no-first-dish' })}
                  className="shrink-0 border-0 bg-transparent p-0 font-body text-xs font-semibold text-orange hover:underline"
                >
                  See all
                </button>
              }
            />
          </div>
          <DataTable
            label="Signed up, no dish yet"
            compact
            minWidth={640}
            columns={NEVER_COLUMNS}
            rows={d.chefsNeverPublished.slice(0, 10)}
            getRowId={(r) => r.profileId}
            onRowClick={(r) => go('/chefs', { view: 'no-first-dish', chef: r.profileId })}
            emptyLabel="Everyone who signed up has published."
          />
        </>
      )}
    </QueryCard>
  );
}
